import { z } from "zod";
import { brandAccessSchema, brandOptionSchema } from "./access.js";
import { connectionClientSchema, connectionKindSchema, keyAbilitiesSchema, keyClientSchema, keyExpirySchema, MOST_REQUESTS_PER_MINUTE, } from "./api-keys.js";
/**
 * A person's own AI app connections, across their workspaces (DESIGN.md "AI
 * apps", agreed 2026-10-14). Anyone may connect an app; one connection reaches
 * any of their workspaces, each with its own brands and switches, and never
 * more than the person holds there.
 *
 * The Rust API answers in exactly these shapes (`server/crates/api/src/modules/
 * api_keys/own_routes.rs`): `GET /api/account/connections`, `POST` the same,
 * `PATCH` and `DELETE /api/account/connections/{id}`, and `POST …/{id}/roll`.
 */
/** How far a connection reaches into one workspace. */
export const reachInputSchema = z.object({
    workspaceId: z.string().min(1),
    brands: brandAccessSchema,
    abilities: keyAbilitiesSchema,
});
export const reachSchema = reachInputSchema.extend({
    id: z.string(),
    /** Null once the person has left the workspace: only removing it is left. */
    workspaceName: z.string().nullable(),
});
export const connectionSchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: connectionKindSchema,
    lastFour: z.string(),
    client: connectionClientSchema,
    requestsPerMinute: z.number().int(),
    expiresAt: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
    createdAt: z.string(),
    reaches: z.array(reachSchema),
});
/** A workspace the person is in, and the most an app of theirs may have there. */
export const reachableWorkspaceSchema = z.object({
    id: z.string(),
    name: z.string(),
    role: z.enum(["owner", "admin", "member"]),
    /** The brands they see, which are the ones they may give. */
    brands: z.array(brandOptionSchema),
    /** True when they see every brand, including brands added later. */
    seesAllBrands: z.boolean(),
    /** The switches they may give an app here. */
    can: keyAbilitiesSchema,
});
export const myConnectionsSchema = z.object({
    connections: z.array(connectionSchema),
    workspaces: z.array(reachableWorkspaceSchema),
    /** Where an MCP app connects. */
    mcpUrl: z.string(),
});
export const connectionInputSchema = z.object({
    name: z.string().trim().min(1).max(80),
    requestsPerMinute: z.number().int().min(1).max(MOST_REQUESTS_PER_MINUTE),
    reaches: z.array(reachInputSchema).min(1).max(50),
});
export const newConnectionSchema = connectionInputSchema.extend({
    client: keyClientSchema,
    expiresIn: keyExpirySchema,
});
/** The secret is in this answer and nowhere else: only its hash is stored. */
export const createdConnectionSchema = z.object({
    connection: connectionSchema,
    secret: z.string(),
});
//# sourceMappingURL=connections.js.map